// src/components/DomainsInfo.js
//
// v3.5.99.7.11: Help bubble for the Domains field on IT-flavored quote pages
// (Managed IT, Bundle, MultiSite — not Voice or FlexIT). Mirrors the modal
// pattern from ThreatProfileInfo verbatim so the two bubbles feel consistent:
//   - (?) icon trigger sized 18×18 with inverted color when modal open
//   - Modal overlay with backdrop click to dismiss
//   - ESC key dismiss bound only while open
//   - Sticky header (title + ×) over scrollable body
//   - Subsection helper component (DomainType) mirrors the Tier component
//
// Purpose: reps need to know which domains we bill for (fully managed: DNS +
// email + website + SSL) and which we don't (parked, forwarded-only). Without
// this content in front of them, the rep either over-counts (inflating MRR
// and getting pushback later) or under-counts (leaving margin on the table).
//
// Wording is intentionally direct — these are scoping rules the rep applies
// during discovery, not customer-facing language. If the rep needs help
// explaining the difference to a client, this content is shareable but the
// formal language for that lives in the Schedule of Services rows.

import React, { useState, useEffect } from 'react';

export default function DomainsInfo() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="What counts as a domain we bill for?"
        style={{
          background: open ? '#0f1e3c' : 'white',
          color: open ? 'white' : '#6b7280',
          border: '1px solid #d1d5db',
          borderRadius: '50%',
          width: 18, height: 18,
          fontSize: 11, fontWeight: 700,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, lineHeight: 1,
          flexShrink: 0,
        }}>
        ?
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              width: '100%',
              maxWidth: 560,
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0,
            }}>
              <h3 style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 700,
                color: '#0f1e3c',
              }}>
                Domain Types &amp; Billing
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close (Esc)"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                  width: 24, height: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                ×
              </button>
            </div>

            <div style={{
              padding: '14px 18px 18px',
              overflowY: 'auto',
              fontSize: 12,
              lineHeight: 1.5,
              color: '#374151',
            }}>
              <div style={{
                fontSize: 11, color: '#6b7280', fontStyle: 'italic',
                marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f1f5f9',
              }}>
                Count <strong>fully managed</strong> domains only. We bill per managed domain
                (DNS records, email routing, website hosting, SSL certificate lifecycle).
                Skip parked and forwarded-only domains — they're $0.
              </div>

              <DomainType
                color="#166534"
                bullet="$"
                label="Fully Managed — Billable"
                descr="Ferrum manages the complete domain lifecycle: DNS records (A, CNAME, MX, TXT), email routing and authentication (SPF, DKIM, DMARC), website hosting or proxying, and SSL certificate provisioning and renewal. Count one for each domain in this state — they drive the per-domain onboarding rate and ongoing oversight."
              />

              <DomainType
                color="#6b7280"
                bullet="—"
                label="Parked — $0"
                descr="Registered but inactive: no email comes or goes from it, no website is hosted on it, no traffic touches it. Ferrum is not actively managing DNS or SSL for the domain. Common for defensive registrations (variant spellings reserved to prevent typosquatting). Do not include in the Domains count."
              />

              <DomainType
                color="#6b7280"
                bullet="↗"
                label="Forwarded Only — $0"
                descr="Redirects to a primary domain (HTTP forward or DNS-level forward). The domain itself has no independent email service and no independent website. Typical for brand redirects, acquired-company domains pointed at the parent, or marketing-variant URLs. Do not include in the Domains count."
              />

              <div style={{
                fontSize: 11, color: '#9ca3af',
                marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9',
                fontStyle: 'italic',
              }}>
                Quick scoping test: <em>"Does email come and go from this domain, AND/OR does it
                host a website Ferrum will support?"</em> If yes to either, it's fully managed.
                If no to both, it's parked or forwarded — $0.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DomainType({ color, bullet, label, descr }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: '50%',
          background: color, color: 'white',
          fontSize: 11, fontWeight: 700, lineHeight: 1,
        }}>{bullet}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{label}</span>
      </div>
      <div style={{ fontSize: 12, color: '#475569', paddingLeft: 26, lineHeight: 1.55 }}>
        {descr}
      </div>
    </div>
  );
}
